import { eventBus } from '../core/eventBus.js';

export class SkillsChart {
  constructor() {
    this.canvas = document.getElementById('skillsRadarChart');
    this.chart = null;
    this.chartInstance = null;
    this.init();
  }

  async init() {
    if (!this.canvas) return;
    await this.loadChartJS();
    if (window.Chart) {
      this.createChart();
      this.bindEvents();
    }
  }

  async loadChartJS() {
    if (window.Chart) return;
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/3.9.1/chart.min.js';
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  createChart() {
    const ctx = this.canvas.getContext('2d');
    const config = this.getChartConfig();
    this.chartInstance = new Chart(ctx, config);
    window.skillsChart = this.chartInstance;
  }

  getChartConfig() {
    const isMobile = window.innerWidth <= 768;
    const isDarkMode = document.body.getAttribute('data-theme') === 'dark';
    const colors = this.getColors(isDarkMode);
    const labels = this.getLabels(isMobile);

    return {
      type: 'radar',
      data: {
        labels: labels,
        datasets: [
          {
            label: '\u00A0\u00A0Industry / Applied',
            data: [80, 60, 60, 50, 55, 15, 80],
            backgroundColor: colors.secondary,
            borderColor: colors.secondaryBorder,
            pointBackgroundColor: colors.secondaryBorder,
            pointBorderColor: '#fff',
            pointBorderWidth: isMobile ? 1 : 2,
            pointRadius: isMobile ? 3 : 4,
            pointHoverRadius: isMobile ? 5 : 6,
            borderWidth: isMobile ? 1 : 2
          },
          {
            label: '\u00A0\u00A0Knownledge',
            data: [80, 75, 60, 55, 65, 25, 80],
            backgroundColor: colors.primary,
            borderColor: colors.primaryBorder,
            pointBackgroundColor: colors.primaryBorder,
            pointBorderColor: '#fff',
            pointBorderWidth: isMobile ? 1 : 2,
            pointRadius: isMobile ? 3 : 4,
            pointHoverRadius: isMobile ? 5 : 6,
            borderWidth: isMobile ? 1 : 2
          }
        ]
      },
      options: this.getChartOptions(isDarkMode, isMobile)
    };
  }

  getColors(isDarkMode) {
    return {
      primary: isDarkMode ? 'rgba(99, 102, 241, 0.30)' : 'rgba(30, 58, 138, 0.30)',
      primaryBorder: isDarkMode ? 'rgba(99, 102, 241, 1)' : 'rgba(30, 58, 138, 1)',
      secondary: isDarkMode ? 'rgba(14, 165, 233, 0.30)' : 'rgba(13, 148, 136, 0.30)',
      secondaryBorder: isDarkMode ? 'rgba(14, 165, 233, 1)' : 'rgba(13, 148, 136, 1)',
      grid: isDarkMode ? 'rgba(156, 163, 175, 0.15)' : 'rgba(209, 213, 219, 0.2)',
      text: isDarkMode ? '#D1D5DB' : '#4B5563'
    };
  }

  getLabels(isMobile) {
    const baseLabels = [
      'Deep Learning / Neural Nets',
      'Applied NLP & LLMs',
      'RL & Robotics',
      'Computer Vision',
      'MLOps / Cloud',
      'Data Engineering',
      'Fullstack & API'
    ];
    if (isMobile) {
      return [
        'DL', 'NLP/LLM', 'RL/Robotics', 'CV', 'MLOps', 'Data Eng.', 'Fullstack'
      ];
    }
    return baseLabels;
  }

  getChartOptions(isDarkMode, isMobile) {
    const colors = this.getColors(isDarkMode);
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: isMobile ? 800 : 1200,
        easing: 'easeOutQuart'
      },
      scales: {
        r: {
          beginAtZero: true,
          max: 100,
          angleLines: { color: colors.grid, lineWidth: 1 },
          grid: { color: colors.grid, lineWidth: 1 },
          pointLabels: {
            color: colors.text,
            font: { family: "'Inter', sans-serif", size: isMobile ? 9 : 12, weight: '500' },
            padding: isMobile ? 8 : 15
          },
          ticks: {
            stepSize: 20,
            backdropColor: 'transparent',
            color: colors.text,
            font: { family: "'Inter', sans-serif", size: isMobile ? 8 : 11, weight: '700' },
            showLabelBackdrop: false
          }
        }
      },
      plugins: {
        legend: {
          display: true,
          position: isMobile ? 'bottom' : 'right',
          labels: {
            color: colors.text,
            font: { family: "'Inter', sans-serif", size: isMobile ? 9 : 12 },
            padding: isMobile ? 15 : 20,
            usePointStyle: true,
            pointStyle: 'circle'
          }
        },
        tooltip: {
          enabled: true,
          backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
          titleColor: colors.text,
          bodyColor: colors.text,
          borderColor: colors.grid,
          borderWidth: 1,
          cornerRadius: 8,
          titleFont: { family: "'Montserrat', sans-serif", size: isMobile ? 10 : 14, weight: 'bold' },
          bodyFont: { family: "'Inter', sans-serif", size: isMobile ? 9 : 13 },
          padding: isMobile ? 8 : 12,
          displayColors: true,
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.r}%`;
            }
          }
        }
      },
      interaction: {
        intersect: false,
        mode: isMobile ? 'nearest' : 'point'
      }
    };
  }

  bindEvents() {
    eventBus.on('theme:changed', () => this.updateTheme());
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this.handleResize(), 250);
    });
  }

  updateTheme() {
    if (!this.chartInstance) return;
    const isDarkMode = document.body.getAttribute('data-theme') === 'dark';
    const colors = this.getColors(isDarkMode);
    this.chartInstance.data.datasets.forEach((dataset, index) => {
      if (index === 0) {
        dataset.backgroundColor = colors.primary;
        dataset.borderColor = colors.primaryBorder;
        dataset.pointBackgroundColor = colors.primaryBorder;
      } else {
        dataset.backgroundColor = colors.secondary;
        dataset.borderColor = colors.secondaryBorder;
        dataset.pointBackgroundColor = colors.secondaryBorder;
      }
    });
    this.chartInstance.options.scales.r.angleLines.color = colors.grid;
    this.chartInstance.options.scales.r.grid.color = colors.grid;
    this.chartInstance.options.scales.r.pointLabels.color = colors.text;
    this.chartInstance.options.scales.r.ticks.color = colors.text;
    this.chartInstance.options.plugins.legend.labels.color = colors.text;
    this.chartInstance.options.plugins.tooltip.backgroundColor = isDarkMode ? '#1F2937' : '#FFFFFF';
    this.chartInstance.options.plugins.tooltip.titleColor = colors.text;
    this.chartInstance.options.plugins.tooltip.bodyColor = colors.text;
    this.chartInstance.options.plugins.tooltip.borderColor = colors.grid;
    this.chartInstance.update('none');
  }

  handleResize() {
    const isMobile = window.innerWidth <= 576;
    const wasMobile = this.chartInstance.options.scales.r.pointLabels.font.size === 9;
    if (isMobile !== wasMobile) {
      this.chartInstance.destroy();
      this.createChart();
    } else {
      this.chartInstance.resize();
    }
  }

  destroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  }
}